import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function startOfWeek(d) {
  const date = new Date(d);
  const day = (date.getDay() + 6) % 7;
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() - day);
  return date;
}

export default function CalendarPage() {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [dragDay, setDragDay] = useState(null);
  const [meetings, setMeetings] = useState([]);

  const refresh = async () => {
    const [r, m] = await Promise.all([api.get("/drafts"), api.get("/ade/meetings").catch(() => ({ data: [] }))]);
    setDrafts(r.data);
    setMeetings(m.data);
  };

  useEffect(() => { refresh().finally(() => setLoading(false)); }, []);

  const weekDays = useMemo(
    () =>
      [...Array(7)].map((_, i) => {
        const d = new Date(weekStart);
        d.setDate(d.getDate() + i);
        return d;
      }),
    [weekStart]
  );

  const byDay = useMemo(() => {
    const map = {};
    weekDays.forEach((d) => (map[d.toDateString()] = []));
    drafts.forEach((d) => {
      if (!d.scheduled_at) return;
      const k = new Date(d.scheduled_at).toDateString();
      if (map[k]) map[k].push(d);
    });
    Object.values(map).forEach((arr) =>
      arr.sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))
    );
    return map;
  }, [drafts, weekDays]);

  const meetingsByDay = useMemo(() => {
    const map = {};
    meetings.forEach((m) => {
      if (!m.start_local) return;
      const k = new Date(m.start_local).toDateString(); // Amsterdam wall-clock date
      (map[k] = map[k] || []).push(m);
    });
    return map;
  }, [meetings]);

  const monthLabel = useMemo(() => {
    const first = weekDays[0], last = weekDays[6];
    const f = first.toLocaleDateString([], { month: "long", year: "numeric" });
    const l = last.toLocaleDateString([], { month: "long", year: "numeric" });
    if (f === l) return f;
    return `${first.toLocaleDateString([], { month: "short" })} – ${last.toLocaleDateString([], { month: "short", year: "numeric" })}`;
  }, [weekDays]);

  const shiftWeek = (delta) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + delta * 7);
    setWeekStart(d);
  };

  const onDragStart = (e, draft) => {
    e.dataTransfer.setData("text/plain", draft.draft_id);
    e.dataTransfer.effectAllowed = "move";
  };

  const onDragOver = (e, day) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragDay(day.toDateString());
  };

  const onDragLeave = () => setDragDay(null);

  const onDrop = async (e, day) => {
    e.preventDefault();
    setDragDay(null);
    const draftId = e.dataTransfer.getData("text/plain");
    if (!draftId) return;
    const d = drafts.find((x) => x.draft_id === draftId);
    if (!d) return;

    // Preserve original time-of-day, move to the dropped date.
    const current = d.scheduled_at ? new Date(d.scheduled_at) : new Date();
    const next = new Date(day);
    next.setHours(current.getHours(), current.getMinutes(), 0, 0);

    // Optimistic update
    setDrafts((prev) =>
      prev.map((x) =>
        x.draft_id === draftId ? { ...x, scheduled_at: next.toISOString() } : x
      )
    );
    try {
      await api.patch(`/drafts/${draftId}/reschedule`, { scheduled_at: next.toISOString() });
      toast.success(
        `Rescheduled to ${next.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" })}`
      );
    } catch {
      toast.error("Reschedule failed");
      refresh();
    }
  };

  return (
    <div className="fade-in" data-testid="calendar-page">
      <div className="flex items-end justify-between mb-10 flex-wrap gap-6">
        <div>
          <div className="label-mono mb-4">Weekly Schedule · drag cards to reschedule</div>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-tight tracking-tight">
            The week ahead.
          </h1>
          <div className="font-display text-xl sm:text-2xl mt-3 text-[#FF3B00]" data-testid="cal-month-label">
            {monthLabel}
            <span className="label-mono text-white/40 ml-3">
              {weekDays[0].toLocaleDateString([], { month: "short", day: "numeric" })} – {weekDays[6].toLocaleDateString([], { month: "short", day: "numeric" })}
            </span>
          </div>
        </div>
        <div className="flex gap-2">
          <button className="btn-ghost-small" onClick={() => shiftWeek(-1)} data-testid="cal-prev-week">← Prev</button>
          <button className="btn-ghost-small" onClick={() => setWeekStart(startOfWeek(new Date()))} data-testid="cal-today">Today</button>
          <button className="btn-ghost-small" onClick={() => shiftWeek(1)} data-testid="cal-next-week">Next →</button>
        </div>
      </div>

      {loading ? (
        <div className="label-mono">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-7 gap-px bg-white/[0.06]">
          {weekDays.map((d, i) => {
            const isToday = d.toDateString() === new Date().toDateString();
            const posts = byDay[d.toDateString()] || [];
            const isHoverTarget = dragDay === d.toDateString();
            return (
              <div
                key={i}
                onDragOver={(e) => onDragOver(e, d)}
                onDragLeave={onDragLeave}
                onDrop={(e) => onDrop(e, d)}
                className={`p-4 min-h-[220px] transition-colors ${
                  isHoverTarget ? "bg-[#FF3B00]/10" : "bg-[#030303]"
                }`}
                data-testid={`cal-day-${i}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="label-mono">{DAYS[i]} <span className="text-white/30">· {d.toLocaleDateString([], { month: "short" })}</span></div>
                    <div className={`font-display text-2xl mt-1 ${isToday ? "text-[#FF3B00]" : ""}`}>
                      {d.getDate()}
                    </div>
                  </div>
                  {posts.length > 0 && (
                    <span className="font-mono text-[10px] text-white/40">{posts.length}</span>
                  )}
                </div>
                <div className="space-y-2">
                  {(meetingsByDay[d.toDateString()] || []).map((m) => (
                    <a
                      key={m.contact_id}
                      href="/ade-outreach"
                      className="block border-l border-amber-400/80 pl-2 py-1 hover:bg-white/[0.04] transition-colors"
                      data-testid={`cal-meeting-${m.contact_id}`}
                      title={m.google_synced ? "Also on Google Calendar" : "Not yet on Google Calendar — press Sync meetings in ADE Outreach"}
                    >
                      <div className="label-mono text-amber-300">
                        {m.time_label} AMS · ADE meeting
                      </div>
                      <div className="text-xs mt-1 line-clamp-2 text-white/85">{m.targetName}</div>
                      <div className="font-mono text-[9px] text-white/40 uppercase tracking-widest mt-0.5">
                        {m.company}{m.location ? ` · ${m.location}` : ""}{m.google_synced ? " · G-Cal ✓" : ""}
                      </div>
                    </a>
                  ))}
                  {posts.map((p) => (
                    <div
                      key={p.draft_id}
                      draggable
                      onDragStart={(e) => onDragStart(e, p)}
                      className="border-l border-[#FF3B00]/60 pl-2 py-1 cursor-grab active:cursor-grabbing hover:bg-white/[0.04] transition-colors"
                      data-testid={`cal-post-${p.draft_id}`}
                    >
                      <div className="label-mono">
                        {new Date(p.scheduled_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </div>
                      <div className="text-xs mt-1 line-clamp-2 text-white/85">
                        {p.release_title}
                      </div>
                      <div className="font-mono text-[9px] text-white/40 uppercase tracking-widest mt-0.5">
                        {p.platform} · {p.status}
                      </div>
                    </div>
                  ))}
                  {posts.length === 0 && !(meetingsByDay[d.toDateString()] || []).length && (
                    <div className="text-white/20 text-xs">—</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
